import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { GlobalStoreService } from '@principle-theorem/ng-principle-shared';
import { TypedFormControl } from '@principle-theorem/ng-shared';
import {
  BillingEntity,
  OrganisationCache,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import {
  type IAccountDetails,
  IInvoice,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  isChanged$,
  isDocRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import {
  combineLatest,
  noop,
  type Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

type InvoiceBillingEntityData = Pick<IInvoice, 'from' | 'attributedTo'>;

@Component({
  selector: 'pr-billing-entity-selector',
  templateUrl: './billing-entity-selector.component.html',
  styleUrls: ['./billing-entity-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
})
export class BillingEntitySelectorComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _invoice$ = new ReplaySubject<WithRef<IInvoice>>(1);
  billingEntityCtrl = new TypedFormControl<InvoiceBillingEntityData>(
    undefined,
    Validators.required
  );
  selectedEntity: Signal<InvoiceBillingEntityData | undefined>;
  billingEntities$: Observable<InvoiceBillingEntityData[]>;
  @Output()
  selectedChanged = new EventEmitter<InvoiceBillingEntityData>();

  @Input()
  set invoice(invoice: WithRef<IInvoice>) {
    if (invoice) {
      this._invoice$.next(invoice);
    }
  }

  constructor(globalStore: GlobalStoreService) {
    this.selectedEntity = toSignal(this.billingEntityCtrl.valueChanges, {
      initialValue: undefined,
    });

    const allocations$ = this._invoice$.pipe(
      map((invoice) => TransactionAllocation.getInvoicedAmounts(invoice))
    );

    this.billingEntities$ = combineLatest([this._invoice$, allocations$]).pipe(
      switchMap(async ([invoice, allocations]) => {
        const practice = await OrganisationCache.practices.getDoc(
          invoice.practice.ref
        );
        const practiceBillingEntities =
          BillingEntity.entitiesForPractice(practice);

        const stafferBillingEntities = await asyncForEach(
          allocations,
          async (allocation) => {
            if (!isDocRef(allocation.allocatedTo)) {
              return [];
            }

            const staffer = await snapshot(
              globalStore.getStaffer$(allocation.allocatedTo)
            );
            return BillingEntity.entitiesForStaffer(staffer);
          }
        );

        return [...stafferBillingEntities.flat(), ...practiceBillingEntities];
      }),
      map((billingEntities) => billingEntities.flat())
    );

    this.billingEntityCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((details) => this.selectedChanged.next(details));

    this._invoice$
      .pipe(
        map((invoice) => invoice.from),
        isChanged$(),
        tap((from) => void this.setSelected(from)),
        takeUntil(this._onDestroy$)
      )
      .subscribe(noop);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async setSelected(details: IAccountDetails): Promise<void> {
    const billingEntity = await snapshot(
      this._findBillingEntityByDetails$(details)
    );

    if (!billingEntity) {
      const billingEntities = await snapshot(this.billingEntities$);
      const selectedEntity = first(billingEntities);
      if (selectedEntity) {
        this.billingEntityCtrl.setValue(selectedEntity);
      }
      return;
    }

    if (billingEntity && billingEntity !== this.billingEntityCtrl.value) {
      this.billingEntityCtrl.setValue(billingEntity);
    }
  }

  compareFn(
    entityA: InvoiceBillingEntityData,
    entityB: InvoiceBillingEntityData
  ): boolean {
    if (entityA.attributedTo && entityB.attributedTo) {
      return (
        entityA.attributedTo.billingEntityUid ===
        entityB.attributedTo.billingEntityUid
      );
    }

    if (entityA.from && entityB.from) {
      return entityA.from.name === entityB.from.name;
    }

    return false;
  }

  private _findBillingEntityByDetails$(
    selectedEntity: IAccountDetails
  ): Observable<InvoiceBillingEntityData | undefined> {
    return this.billingEntities$.pipe(
      map((billingEntities) =>
        billingEntities.find((billingEntity) => {
          return this._isSame(selectedEntity, billingEntity.from);
        })
      )
    );
  }

  private _isSame(
    detailsA: IAccountDetails,
    detailsB: IAccountDetails
  ): boolean {
    return detailsA.name === detailsB.name && detailsA.taxId === detailsB.taxId;
  }
}
